/**
 * useApproachMoments — integration hook wiring the pure approach/arrival state
 * machine (app/services/map/approach-moments.ts) into the live tracking map.
 * Part of the Experimental Premium Live Map Layer (PLA-262 / PLA-267).
 *
 * The hook re-derives the approach state on every driver location tick (the
 * distance input is the SAME value useFollowCamera already computes per tick —
 * see its `distanceToDestinationMeters` result) and exposes render-ready
 * primitives only:
 *
 * - `pulseIntensity` — destination-pin halo energy (0 far … 1 arrived).
 * - `leg`            — current tracking leg as confirmed by the machine; flips
 *                      exactly when the machine emits its one-shot `roleSwap`.
 * - `dockingSignal`  — monotonic counter, bumped once per leg when the car
 *                      crosses into "arrived" (renderer cue for the docking
 *                      settle). Never bumps under prefers-reduced-motion —
 *                      the machine's `reducedMotion` config suppresses it.
 * - `roleSwapSignal` — monotonic counter, bumped on each roleSwap emission.
 * - `reducedMotion`  — live prefers-reduced-motion flag so consumers replace
 *                      flourishes with instant changes. State logic always
 *                      runs regardless of this flag.
 *
 * `enabled: false` (FleetHub multi-order view, all-locations intro, driveby)
 * keeps the machine reset and all outputs inert — the approach choreography is
 * single-order B2C only.
 */

import { useEffect, useRef, useState } from "react"

import {
  ApproachState,
  deriveApproachState,
  dockingTriggered,
  pulseIntensity as machinePulseIntensity,
  TrackingLeg,
} from "@/services/map/approach-moments"
import { prefersReducedMotion, subscribeReducedMotion } from "@/utils/reducedMotion"

export interface UseApproachMomentsOptions {
  /**
   * Distance (meters) from the vehicle to the ACTIVE destination, recomputed
   * per driver tick — the customer pin on the pickup/return legs, the hub pin on
   * the customer -> hub ("toHub") leg. Must be computed from the SAME tick +
   * destination as `leg` (the caller derives it in-render to avoid a
   * leg/distance mismatch). Null (no tick yet) holds the machine, per the
   * approach-moments contract.
   */
  distanceToDestinationMeters: number | null
  /** True while the order is in a live-driving phase (en route / return). */
  isLiveDriving: boolean
  /** Current tracking leg: "pickup" | "toHub" | "return" (a change resets state). */
  leg: TrackingLeg
  /** False outside single-order B2C live tracking — outputs stay inert. */
  enabled: boolean
}

export interface UseApproachMomentsResult {
  /** Destination-pin halo energy: 0, 0.33, 0.66 or 1. 0 while disabled. */
  pulseIntensity: number
  /** Machine-confirmed current leg (flips exactly on roleSwap). */
  leg: TrackingLeg
  /** Bumped once per leg on the transition into "arrived". */
  dockingSignal: number
  /** Bumped once per leg change (pickup -> return role swap). */
  roleSwapSignal: number
  /** Live prefers-reduced-motion flag (state logic runs either way). */
  reducedMotion: boolean
}

export function useApproachMoments(options: UseApproachMomentsOptions): UseApproachMomentsResult {
  const { distanceToDestinationMeters, isLiveDriving, leg, enabled } = options

  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion)
  useEffect(() => subscribeReducedMotion(setReducedMotion), [])

  // Machine state lives in a ref; only render-relevant primitives are mirrored
  // into React state so consumers re-render exactly when the visuals change.
  const stateRef = useRef<ApproachState | null>(null)
  const [pulse, setPulse] = useState(0)
  const [machineLeg, setMachineLeg] = useState<TrackingLeg>(leg)
  const [dockingSignal, setDockingSignal] = useState(0)
  const [roleSwapSignal, setRoleSwapSignal] = useState(0)

  useEffect(() => {
    if (!enabled) {
      // Choreography off (multi-order / intro / driveby): reset the machine so
      // a later enable starts a fresh leg, and pin the halo at idle.
      stateRef.current = null
      setPulse(0)
      return
    }

    const prev = stateRef.current
    const next = deriveApproachState(
      prev,
      {
        // Null (no tick yet) maps to NaN: the machine holds by contract.
        distanceToDestinationMeters: distanceToDestinationMeters ?? Number.NaN,
        isLiveDriving,
        leg,
      },
      { reducedMotion },
    )
    stateRef.current = next

    setPulse(machinePulseIntensity(next))
    setMachineLeg(next.leg)
    if (dockingTriggered(prev, next, { reducedMotion })) {
      setDockingSignal((n) => n + 1)
    }
    if (next.roleSwap) {
      setRoleSwapSignal((n) => n + 1)
    }
  }, [enabled, distanceToDestinationMeters, isLiveDriving, leg, reducedMotion])

  return {
    pulseIntensity: pulse,
    leg: machineLeg,
    dockingSignal,
    roleSwapSignal,
    reducedMotion,
  }
}
